import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Workflow, Plus, Edit, Trash2, CheckCircle2, XCircle, Clock, RefreshCw,
  Loader2, AlertTriangle, Shield, FileText, DollarSign, Users, Settings
} from 'lucide-react';
import { salesWorkflowAPI } from '../api';

const ruleTypeLabels = {
  quote_approval: 'Quote Approval',
  deal_approval: 'Deal Approval',
  discount_approval: 'Discount Approval',
  commission_approval: 'Commission Approval',
  prospect_assignment: 'Prospect Assignment',
  stage_change: 'Stage Change'
};

const ruleTypeColors = {
  quote_approval: 'bg-blue-100 text-blue-800',
  deal_approval: 'bg-purple-100 text-purple-800',
  discount_approval: 'bg-orange-100 text-orange-800',
  commission_approval: 'bg-green-100 text-green-800',
  prospect_assignment: 'bg-rose-100 text-rose-800',
  stage_change: 'bg-cyan-100 text-cyan-800'
};

const approvalStatusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  auto_approved: 'bg-blue-100 text-blue-800'
};

export const SalesWorkflowRulesPage = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('rules');
  
  // Data state
  const [rules, setRules] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [typeCounts, setTypeCounts] = useState({});
  const [approvalStats, setApprovalStats] = useState({});
  
  // Dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [creating, setCreating] = useState(false);
  
  // Form state
  const [ruleForm, setRuleForm] = useState({
    name: '',
    description: '',
    rule_type: 'quote_approval',
    is_active: true,
    priority: 0,
    conditions: [],
    requires_approval: true,
    approver_roles: ['admin', 'sales_manager'],
    auto_approve_threshold: null,
    notify_on_trigger: true
  });
  
  // New condition form
  const [conditionForm, setConditionForm] = useState({
    field: '',
    operator: 'equals',
    value: ''
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      if (activeTab === 'rules') {
        const response = await salesWorkflowAPI.getRules({});
        setRules(response.data.rules || []);
        setTypeCounts(response.data.type_counts || {});
      } else {
        const response = await salesWorkflowAPI.getApprovals({});
        setApprovals(response.data.requests || []);
        setApprovalStats(response.data.stats || {});
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load workflow data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePresets = async () => {
    try {
      const response = await salesWorkflowAPI.createPresets();
      toast.success(`Created ${response.data.count} preset rules`);
      fetchData();
    } catch (error) {
      console.error('Failed to create presets:', error);
      toast.error('Failed to create preset rules');
    }
  };

  const handleAddCondition = () => {
    if (!conditionForm.field || !conditionForm.value) {
      toast.error('Field and value are required');
      return;
    }
    
    setRuleForm(prev => ({
      ...prev,
      conditions: [...prev.conditions, { ...conditionForm }]
    }));
    
    setConditionForm({ field: '', operator: 'equals', value: '' });
  };

  const handleRemoveCondition = (index) => {
    setRuleForm(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
  };

  const handleCreateRule = async () => {
    if (!ruleForm.name) {
      toast.error('Rule name is required');
      return;
    }
    
    try {
      setCreating(true);
      
      if (editingRule) {
        await salesWorkflowAPI.updateRule(editingRule.id, ruleForm);
        toast.success('Rule updated successfully');
      } else {
        await salesWorkflowAPI.createRule(ruleForm);
        toast.success('Rule created successfully');
      }
      
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save rule:', error);
      toast.error('Failed to save rule');
    } finally {
      setCreating(false);
    }
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setRuleForm({
      name: rule.name,
      description: rule.description || '',
      rule_type: rule.rule_type,
      is_active: rule.is_active,
      priority: rule.priority,
      conditions: rule.conditions || [],
      requires_approval: rule.requires_approval,
      approver_roles: rule.approver_roles || [],
      auto_approve_threshold: rule.auto_approve_threshold,
      notify_on_trigger: rule.notify_on_trigger
    });
    setShowCreateDialog(true);
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Delete this rule?')) return;
    
    try {
      await salesWorkflowAPI.deleteRule(ruleId);
      toast.success('Rule deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      await salesWorkflowAPI.updateRule(rule.id, { is_active: !rule.is_active });
      toast.success(`Rule ${rule.is_active ? 'disabled' : 'enabled'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update rule');
    }
  };

  const handleApprove = async (requestId) => {
    try {
      await salesWorkflowAPI.approve(requestId);
      toast.success('Request approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve request');
    }
  };

  const handleReject = async (requestId) => {
    const reason = window.prompt('Enter rejection reason (optional):');
    try {
      await salesWorkflowAPI.reject(requestId, reason);
      toast.success('Request rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  const resetForm = () => {
    setEditingRule(null);
    setRuleForm({
      name: '',
      description: '',
      rule_type: 'quote_approval',
      is_active: true,
      priority: 0,
      conditions: [],
      requires_approval: true,
      approver_roles: ['admin', 'sales_manager'],
      auto_approve_threshold: null,
      notify_on_trigger: true
    });
    setConditionForm({ field: '', operator: 'equals', value: '' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sales-workflow-rules">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Workflow className="h-6 w-6" />
            Sales Workflow Rules
          </h1>
          <p className="text-muted-foreground">
            Configure approval workflows for quotes, deals, and commissions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {hasRole(['admin']) && rules.length === 0 && (
            <Button variant="outline" onClick={handleCreatePresets}>
              <Settings className="h-4 w-4 mr-2" />
              Create Presets
            </Button>
          )}
          {hasRole(['admin', 'sales_manager']) && (
            <Button onClick={() => { resetForm(); setShowCreateDialog(true); }} data-testid="create-rule-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Rule
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
        {Object.entries(typeCounts).map(([type, count]) => (
          <Card key={type} className="cursor-pointer hover:bg-slate-50">
            <CardContent className="pt-4 pb-4 text-center">
              <p className="text-xl font-bold">{count}</p>
              <p className="text-xs text-muted-foreground">{ruleTypeLabels[type] || type}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="rules" className="flex items-center gap-2">
            <Workflow className="h-4 w-4" />
            Rules ({rules.length})
          </TabsTrigger>
          <TabsTrigger value="approvals" className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            Approvals ({approvalStats.pending || 0} pending)
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="rules" className="mt-4">
          <Card>
            <CardContent className="p-0">
              {rules.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Workflow className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No workflow rules configured</p>
                  <p className="text-sm">Create rules to automate approvals</p>
                  <div className="flex gap-2 justify-center mt-4">
                    <Button variant="outline" onClick={handleCreatePresets}>
                      Load Preset Rules
                    </Button>
                    <Button onClick={() => { resetForm(); setShowCreateDialog(true); }}>
                      Create Custom Rule
                    </Button>
                  </div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rule Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Conditions</TableHead>
                      <TableHead>Auto-Approve</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rules.map((rule) => (
                      <TableRow key={rule.id} data-testid={`rule-row-${rule.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{rule.name}</p>
                            {rule.description && (
                              <p className="text-xs text-muted-foreground">{rule.description}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={ruleTypeColors[rule.rule_type]}>
                            {ruleTypeLabels[rule.rule_type]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {rule.conditions?.length > 0 ? (
                            <span className="text-sm">{rule.conditions.length} condition(s)</span>
                          ) : (
                            <span className="text-sm text-muted-foreground">No conditions</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {rule.auto_approve_threshold ? (
                            <span className="text-sm">${rule.auto_approve_threshold.toLocaleString()}</span>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={rule.is_active}
                            onCheckedChange={() => handleToggleActive(rule)}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center gap-1 justify-end">
                            <Button variant="ghost" size="icon" onClick={() => handleEditRule(rule)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteRule(rule.id)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="approvals" className="mt-4">
          <Card>
            <CardContent className="p-0">
              {approvals.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No approval requests</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Request</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Requested By</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {approvals.map((approval) => (
                      <TableRow key={approval.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{approval.entity_name}</p>
                            <p className="text-xs text-muted-foreground">{approval.rule_name}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={ruleTypeColors[approval.rule_type]}>
                            {ruleTypeLabels[approval.rule_type]}
                          </Badge>
                        </TableCell>
                        <TableCell>{approval.requested_by_name}</TableCell>
                        <TableCell>
                          <Badge className={approvalStatusColors[approval.status]}>
                            {approval.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {format(new Date(approval.created_at), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell className="text-right">
                          {approval.status === 'pending' && (
                            <div className="flex items-center gap-1 justify-end">
                              <Button variant="ghost" size="icon" onClick={() => handleApprove(approval.id)}>
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleReject(approval.id)}>
                                <XCircle className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Rule Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Rule' : 'Create Workflow Rule'}</DialogTitle>
            <DialogDescription>
              Configure conditions and approval requirements
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Rule Name *</Label>
                <Input
                  id="name"
                  value={ruleForm.name}
                  onChange={(e) => setRuleForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Manager Approval for Large Discounts"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={ruleForm.description}
                  onChange={(e) => setRuleForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe when this rule applies..."
                  rows={2}
                />
              </div>
              <div>
                <Label>Rule Type</Label>
                <Select 
                  value={ruleForm.rule_type} 
                  onValueChange={(v) => setRuleForm(prev => ({ ...prev, rule_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quote_approval">Quote Approval</SelectItem>
                    <SelectItem value="deal_approval">Deal Approval</SelectItem>
                    <SelectItem value="discount_approval">Discount Approval</SelectItem>
                    <SelectItem value="commission_approval">Commission Approval</SelectItem>
                    <SelectItem value="prospect_assignment">Prospect Assignment</SelectItem>
                    <SelectItem value="stage_change">Stage Change</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="priority">Priority (lower = higher)</Label>
                <Input
                  id="priority"
                  type="number"
                  value={ruleForm.priority}
                  onChange={(e) => setRuleForm(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                  min={0}
                />
              </div>
            </div>
            
            {/* Conditions */}
            <div className="border rounded-lg p-4">
              <Label className="mb-2 block">Conditions</Label>
              
              {ruleForm.conditions.length > 0 && (
                <div className="space-y-2 mb-4">
                  {ruleForm.conditions.map((cond, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                      <span className="text-sm flex-1">
                        {cond.field} {cond.operator} {cond.value}
                      </span>
                      <Button variant="ghost" size="icon" onClick={() => handleRemoveCondition(index)}>
                        <XCircle className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label className="text-xs">Field</Label>
                  <Input
                    value={conditionForm.field}
                    onChange={(e) => setConditionForm(prev => ({ ...prev, field: e.target.value }))}
                    placeholder="e.g., total, discount_percent, value"
                    className="h-9"
                  />
                </div>
                <div className="w-32">
                  <Label className="text-xs">Operator</Label>
                  <Select 
                    value={conditionForm.operator} 
                    onValueChange={(v) => setConditionForm(prev => ({ ...prev, operator: v }))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="equals">=</SelectItem>
                      <SelectItem value="not_equals">≠</SelectItem>
                      <SelectItem value="greater_than">&gt;</SelectItem>
                      <SelectItem value="less_than">&lt;</SelectItem>
                      <SelectItem value="greater_or_equal">≥</SelectItem>
                      <SelectItem value="less_or_equal">≤</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-24">
                  <Label className="text-xs">Value</Label>
                  <Input
                    value={conditionForm.value}
                    onChange={(e) => setConditionForm(prev => ({ ...prev, value: e.target.value }))}
                    className="h-9"
                  />
                </div>
                <Button variant="outline" size="sm" onClick={handleAddCondition}>
                  Add
                </Button>
              </div>
            </div>
            
            {/* Approval Settings */}
            <div className="space-y-4 border rounded-lg p-4">
              <Label>Approval Settings</Label>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Requires Approval</p>
                  <p className="text-xs text-muted-foreground">Items matching this rule need approval</p>
                </div>
                <Switch
                  checked={ruleForm.requires_approval}
                  onCheckedChange={(v) => setRuleForm(prev => ({ ...prev, requires_approval: v }))}
                />
              </div>
              
              {ruleForm.requires_approval && (
                <>
                  <div>
                    <Label htmlFor="auto_threshold">Auto-Approve Threshold ($)</Label>
                    <Input
                      id="auto_threshold"
                      type="number"
                      value={ruleForm.auto_approve_threshold || ''}
                      onChange={(e) => setRuleForm(prev => ({ 
                        ...prev, 
                        auto_approve_threshold: e.target.value ? parseFloat(e.target.value) : null 
                      }))}
                      placeholder="Auto-approve if under this amount"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Leave empty to always require approval</p>
                  </div>
                  
                  <div>
                    <Label>Approver Roles</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {['admin', 'sales_manager', 'team_lead'].map(role => (
                        <Badge
                          key={role}
                          variant={ruleForm.approver_roles.includes(role) ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => {
                            setRuleForm(prev => ({
                              ...prev,
                              approver_roles: prev.approver_roles.includes(role)
                                ? prev.approver_roles.filter(r => r !== role)
                                : [...prev.approver_roles, role]
                            }));
                          }}
                        >
                          {role.replace('_', ' ')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </>
              )}
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Send Notifications</p>
                  <p className="text-xs text-muted-foreground">Notify when this rule triggers</p>
                </div>
                <Switch
                  checked={ruleForm.notify_on_trigger}
                  onCheckedChange={(v) => setRuleForm(prev => ({ ...prev, notify_on_trigger: v }))}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleCreateRule} disabled={creating}>
              {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              {editingRule ? 'Update Rule' : 'Create Rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesWorkflowRulesPage;
